import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card } from "react-bootstrap";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { LinkedAlgo } from "../types";
import { ArrowLeft, Copy } from "lucide-react";

const AlgoCode: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [algo, setAlgo] = useState<LinkedAlgo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAlgo();
  }, [id]);

  const fetchAlgo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BACKEND_URL}/api/linked-algos/${id}`);
      setAlgo(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch algo details");
    } finally {
      setLoading(false);
    }
  };

  const generateCompleteCode = (algo: LinkedAlgo) => {
    let code = `#!/usr/bin/env python3\n`;
    code += `"""\n`;
    code += `Trading Algorithm: ${algo.name}\n`;
    code += `Generated on: ${new Date().toISOString()}\n`;
    code += `Description: ${algo.description || "No description available"}\n`;
    code += `"""\n\n`;

    // Imports
    code += `import pandas as pd\n`;
    code += `import numpy as np\n`;
    code += `from datetime import datetime, timedelta\n`;
    code += `from typing import Dict, List, Optional, Tuple\n`;
    code += `import logging\n\n`;

    // Configuration
    code += `# Algorithm Configuration\n`;
    code += `ALGO_NAME = "${algo.name}"\n`;
    code += `ALGO_DESCRIPTION = "${
      algo.description || "No description available"
    }"\n`;
    code += `TIMEFRAME = "${algo.timeframe || "1h"}"\n\n`;

    // Asset Classes and Symbols
    const assetClasses = algo.parameters?.assetClasses || [];
    const symbols = algo.parameters?.symbols || [];

    code += `# Asset Classes\n`;
    code += `ASSET_CLASSES = [\n`;
    assetClasses.forEach((ac: any) => {
      code += `    {"type": "${ac.type}", "config": ${JSON.stringify(
        ac.config,
        null,
        4
      )}},\n`;
    });
    code += `]\n\n`;

    code += `# Trading Symbols\n`;
    code += `SYMBOLS = [\n`;
    symbols.forEach((symbol: any) => {
      code += `    {"symbol": "${symbol.symbol}", "assetClass": "${symbol.assetClass}", "maxCapital": ${symbol.maxCapital}},\n`;
    });
    code += `]\n\n`;

    // Entry Conditions
    const entryConditions = algo.parameters?.algorithm?.entryConditions;
    if (entryConditions) {
      code += `def check_entry_conditions(data: pd.DataFrame, symbol: str) -> Optional[str]:\n`;
      code += `    """Entry conditions for ${algo.name}"""\n`;
      code += `    try:\n`;

      if (entryConditions.buyConditions?.length > 0) {
        code += `        # Buy Conditions\n`;
        entryConditions.buyConditions.forEach(
          (condition: any, index: number) => {
            code += `        if ${condition.indicator1?.type || "indicator1"}(${
              condition.indicator1?.period || "period"
            }) ${condition.relationship || "crosses_above"} ${
              condition.indicator2?.type || "indicator2"
            }(${condition.indicator2?.period || "period"}):\n`;
            code += `            return "BUY"\n\n`;
          }
        );
      }

      if (entryConditions.sellConditions?.length > 0) {
        code += `        # Sell Conditions\n`;
        entryConditions.sellConditions.forEach(
          (condition: any, index: number) => {
            code += `        if ${condition.indicator1?.type || "indicator1"}(${
              condition.indicator1?.period || "period"
            }) ${condition.relationship || "crosses_below"} ${
              condition.indicator2?.type || "indicator2"
            }(${condition.indicator2?.period || "period"}):\n`;
            code += `            return "SELL"\n\n`;
          }
        );
      }

      code += `        return None  # No entry signal\n`;
      code += `    except Exception as e:\n`;
      code += `        logging.error(f"Error in entry conditions: {e}")\n`;
      code += `        return None\n\n`;
    } else {
      code += `def check_entry_conditions(data: pd.DataFrame, symbol: str) -> Optional[str]:\n`;
      code += `    """Entry conditions for ${algo.name}"""\n`;
      code += `    # No entry conditions configured\n`;
      code += `    return None\n\n`;
    }

    // Exit Conditions
    const exitConditions = algo.parameters?.algorithm?.exitConditions;
    if (exitConditions) {
      code += `def check_exit_conditions(data: pd.DataFrame, symbol: str, position: str) -> Optional[str]:\n`;
      code += `    """Exit conditions for ${algo.name}"""\n`;
      code += `    try:\n`;

      if (exitConditions.buyExitConditions?.length > 0) {
        code += `        # Exit Buy Position Conditions\n`;
        exitConditions.buyExitConditions.forEach(
          (condition: any, index: number) => {
            code += `        if ${condition.indicator1?.type || "indicator1"}(${
              condition.indicator1?.period || "period"
            }) ${condition.relationship || "crosses_below"} ${
              condition.indicator2?.type || "indicator2"
            }(${condition.indicator2?.period || "period"}):\n`;
            code += `            return "CLOSE_LONG"\n\n`;
          }
        );
      }

      if (exitConditions.sellExitConditions?.length > 0) {
        code += `        # Exit Sell Position Conditions\n`;
        exitConditions.sellExitConditions.forEach(
          (condition: any, index: number) => {
            code += `        if ${condition.indicator1?.type || "indicator1"}(${
              condition.indicator1?.period || "period"
            }) ${condition.relationship || "crosses_above"} ${
              condition.indicator2?.type || "indicator2"
            }(${condition.indicator2?.period || "period"}):\n`;
            code += `            return "CLOSE_SHORT"\n\n`;
          }
        );
      }

      code += `        return None  # No exit signal\n`;
      code += `    except Exception as e:\n`;
      code += `        logging.error(f"Error in exit conditions: {e}")\n`;
      code += `        return None\n\n`;
    } else {
      code += `def check_exit_conditions(data: pd.DataFrame, symbol: str, position: str) -> Optional[str]:\n`;
      code += `    """Exit conditions for ${algo.name}"""\n`;
      code += `    # No exit conditions configured\n`;
      code += `    return None\n\n`;
    }

    // Risk Management
    const risk = algo.parameters?.risk;
    if (risk) {
      code += `def calculate_position_size(account_balance: float, symbol: str) -> Dict[str, any]:\n`;
      code += `    """Risk management for ${algo.name}"""\n`;
      code += `    try:\n`;
      code += `        # Position Sizing\n`;
      code += `        risk_source = "${risk.riskSource || "balance"}"\n`;
      code += `        lot_mode = "${risk.lotMode || "fixed"}"\n`;
      code += `        lot_value = ${risk.lotValue || 0}\n`;
      code += `        risk_percentage = ${risk.riskPercentage || 0}\n`;
      code += `        max_dollar_risk = ${risk.maxDollarRisk || 0}\n\n`;

      code += `        # Stop Loss & Take Profit\n`;
      code += `        stop_loss_percentage = ${
        risk.stopLossPercentage || 0
      }\n`;
      code += `        take_profit_percentage = ${
        risk.takeProfitPercentage || 0
      }\n`;
      code += `        breakeven_after_profit = ${
        risk.breakevenAfterProfit || 0
      }\n`;
      code += `        close_trade_after_drawdown = ${
        risk.closeTradeAfterDrawdown || 0
      }\n\n`;

      code += `        # Trailing Stop\n`;
      code += `        trailing_stop_enabled = ${
        risk.trailingStopEnabled || false
      }\n`;
      code += `        trailing_stop_amount = ${
        risk.trailingStopAmount || 0
      }\n`;
      code += `        trailing_take_profit_enabled = ${
        risk.trailingTakeProfitEnabled || false
      }\n`;
      code += `        trailing_take_profit_amount = ${
        risk.trailingTakeProfitAmount || 0
      }\n\n`;

      code += `        # Order Settings\n`;
      code += `        order_type = "${risk.orderType || "market"}"\n`;
      code += `        limit_price_offset = ${risk.limitPriceOffset || 0}\n`;
      code += `        allow_reversal = ${risk.allowReversal || false}\n\n`;

      code += `        return {\n`;
      code += `            'risk_source': risk_source,\n`;
      code += `            'lot_mode': lot_mode,\n`;
      code += `            'lot_value': lot_value,\n`;
      code += `            'risk_percentage': risk_percentage,\n`;
      code += `            'max_dollar_risk': max_dollar_risk,\n`;
      code += `            'stop_loss_percentage': stop_loss_percentage,\n`;
      code += `            'take_profit_percentage': take_profit_percentage,\n`;
      code += `            'trailing_stop_enabled': trailing_stop_enabled,\n`;
      code += `            'trailing_stop_amount': trailing_stop_amount,\n`;
      code += `            'order_type': order_type\n`;
      code += `        }\n`;
      code += `    except Exception as e:\n`;
      code += `        logging.error(f"Error in risk management: {e}")\n`;
      code += `        return {}\n\n`;
    } else {
      code += `def calculate_position_size(account_balance: float, symbol: str) -> Dict[str, any]:\n`;
      code += `    """Risk management for ${algo.name}"""\n`;
      code += `    # No risk parameters configured\n`;
      code += `    return {}\n\n`;
    }

    // Trading Schedule
    const schedule = algo.parameters?.schedule;
    if (schedule) {
      code += `def check_trading_schedule() -> bool:\n`;
      code += `    """Trading schedule for ${algo.name}"""\n`;
      code += `    try:\n`;
      code += `        # Schedule Settings\n`;
      code += `        active_shutdown = ${schedule.activeShutdown || false}\n`;
      code += `        close_day = "${schedule.closeDay || "Friday"}"\n`;
      code += `        close_time = "${schedule.closeTime || "16:00"}"\n`;
      code += `        restart_day = "${schedule.restartDay || "Monday"}"\n`;
      code += `        restart_time = "${schedule.restartTime || "09:00"}"\n`;
      code += `        daily_start_time = "${
        schedule.dailyStartTime || "09:00"
      }"\n`;
      code += `        daily_stop_time = "${
        schedule.dailyStopTime || "16:00"
      }"\n`;
      code += `        weekly_shutdown = ${schedule.weeklyShutdown || false}\n`;
      code += `        market_open_time = "${
        schedule.marketOpenTime || "09:00"
      }"\n`;
      code += `        market_close_time = "${
        schedule.marketCloseTime || "16:00"
      }"\n\n`;

      code += `        # Check if trading is allowed\n`;
      code += `        current_time = datetime.now()\n`;
      code += `        current_day = current_time.strftime('%A')\n`;
      code += `        current_time_str = current_time.strftime('%H:%M')\n\n`;

      code += `        # Check daily schedule\n`;
      code += `        if current_time_str < daily_start_time or current_time_str > daily_stop_time:\n`;
      code += `            return False\n\n`;

      code += `        # Check weekly shutdown\n`;
      code += `        if weekly_shutdown and current_day in ['Saturday', 'Sunday']:\n`;
      code += `            return False\n\n`;

      code += `        # Check active shutdown\n`;
      code += `        if active_shutdown and current_day == close_day and current_time_str >= close_time:\n`;
      code += `            return False\n\n`;

      code += `        return True  # Trading allowed\n`;
      code += `    except Exception as e:\n`;
      code += `        logging.error(f"Error in trading schedule: {e}")\n`;
      code += `        return False\n\n`;
    } else {
      code += `def check_trading_schedule() -> bool:\n`;
      code += `    """Trading schedule for ${algo.name}"""\n`;
      code += `    # No schedule parameters configured\n`;
      code += `    return True\n\n`;
    }

    // Main Trading Function
    code += `def execute_trading_logic(data: pd.DataFrame, symbol: str, account_balance: float) -> Dict[str, any]:\n`;
    code += `    """Main trading logic for ${algo.name}"""\n`;
    code += `    try:\n`;
    code += `        # Check if trading is allowed\n`;
    code += `        if not check_trading_schedule():\n`;
    code += `            return {"action": "WAIT", "reason": "Outside trading hours"}\n\n`;
    code += `        # Check entry conditions\n`;
    code += `        entry_signal = check_entry_conditions(data, symbol)\n`;
    code += `        if entry_signal:\n`;
    code += `            risk_params = calculate_position_size(account_balance, symbol)\n`;
    code += `            return {\n`;
    code += `                "action": entry_signal,\n`;
    code += `                "risk_params": risk_params,\n`;
    code += `                "symbol": symbol\n`;
    code += `            }\n\n`;
    code += `        # Check exit conditions (if in position)\n`;
    code += `        # This would be called separately when monitoring existing positions\n`;
    code += `        return {"action": "HOLD", "reason": "No signal"}\n`;
    code += `    except Exception as e:\n`;
    code += `        logging.error(f"Error in trading logic: {e}")\n`;
    code += `        return {"action": "ERROR", "reason": str(e)}\n\n`;

    // Example Usage
    code += `if __name__ == "__main__":\n`;
    code += `    # Example usage\n`;
    code += `    logging.basicConfig(level=logging.INFO)\n`;
    code += `    print(f"Trading Algorithm: {ALGO_NAME}")\n`;
    code += `    print(f"Description: {ALGO_DESCRIPTION}")\n`;
    code += `    print(f"Timeframe: {TIMEFRAME}")\n`;
    code += `    print(f"Asset Classes: {len(ASSET_CLASSES)}")\n`;
    code += `    print(f"Symbols: {len(SYMBOLS)}")\n`;

    return code;
  };

  const handleCopyCode = async () => {
    if (!algo) return;

    try {
      const code = generateCompleteCode(algo);
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!algo) return <div>Algo not found</div>;

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            <ArrowLeft size={16} className="me-1" />
            Back
          </Button>
          <Typography variant="h4">{algo.name} - Python Code</Typography>
        </div>
        <Button
          variant="outline-primary"
          onClick={handleCopyCode}
          className="d-flex align-items-center"
          disabled={copied}
        >
          <Copy size={16} className="me-1" />
          {copied ? "Copied!" : "Copy Code"}
        </Button>
      </div>

      <Card>
        <Card.Body>
          <Typography variant="h6" className="mb-3">
            Complete Trading Algorithm Code
          </Typography>
          <pre
            style={{
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              padding: "20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "Consolas, Monaco, 'Courier New', monospace",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {generateCompleteCode(algo)}
          </pre>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AlgoCode;
